import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

const SOURCE_LANGUAGE = 'en-us';
const ASSETS_BASE = '/assets/i18n/';
const KEYS_URL = `${ASSETS_BASE}keys.json`;

/**
 * Translation loader that resolves every key through a fallback chain:
 *
 *   <selected language>  ▷ overrides ▷  en-us  ▷ overrides ▷  keys.json
 *
 * The per-language files (`<lang>.json`, `en-us.json`) are served from a
 * CloudFront distribution configured via `baseUrl`. `keys.json` is always read
 * from the bundled assets – it is the canonical key set and the ultimate
 * fallback. When no base URL is set the per-language files are read from the
 * bundled `/assets/i18n/` folder too (used for local development).
 *
 * Empty or missing values fall through to the next layer, so a partially
 * translated language still renders English (or, as a last resort, the source
 * text shipped in `keys.json`).
 */
export class CloudfrontTranslateLoader implements TranslateLoader {
    private readonly base: string;

    constructor(
        private readonly http: HttpClient,
        baseUrl?: string
    ) {
        this.base = normalizeBase(baseUrl);
    }

    public getTranslation(lang: string): Observable<TranslationObject> {
        const keys$ = this.fetch(KEYS_URL);
        const source$ = lang === SOURCE_LANGUAGE ? of<TranslationObject>({}) : this.fetchLanguage(SOURCE_LANGUAGE);
        const language$ = this.fetchLanguage(lang);

        return forkJoin([keys$, source$, language$]).pipe(
            map(([keys, source, language]) => mergeLayers(keys, source, language))
        );
    }

    /**
     * Fetch a single language file from the configured base (the CloudFront
     * distribution, or the bundled `/assets/i18n/` folder when no base URL is
     * set). A missing or failing file resolves to an empty object so the next
     * fallback layer takes over.
     */
    private fetchLanguage(lang: string): Observable<TranslationObject> {
        return this.fetch(`${this.base}${lang}.json`);
    }

    private fetch(url: string): Observable<TranslationObject> {
        return this.http.get<TranslationObject>(url).pipe(catchError(() => of<TranslationObject>({})));
    }
}

function normalizeBase(baseUrl?: string): string {
    const trimmed = baseUrl?.trim();
    if (!trimmed) {
        return ASSETS_BASE;
    }
    return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
}

/**
 * Deep-merges translation layers in priority order (last wins). A string only
 * overrides a previous layer when it is non-empty, so blank translations fall
 * through to the fallback layer rather than masking it.
 */
function mergeLayers(...layers: TranslationObject[]): TranslationObject {
    const result: TranslationObject = {};
    for (const layer of layers) {
        deepMerge(result, layer);
    }
    return result;
}

function deepMerge(target: TranslationObject, source: TranslationObject): void {
    for (const [key, value] of Object.entries(source)) {
        if (isTranslationObject(value)) {
            const existing = target[key];
            const branch = isTranslationObject(existing) ? existing : {};
            deepMerge(branch, value);
            target[key] = branch;
        } else if (typeof value === 'string') {
            if (value.trim() !== '') {
                target[key] = value;
            }
        } else if (value != null) {
            target[key] = value;
        }
    }
}

function isTranslationObject(value: unknown): value is TranslationObject {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
